import React, { useState, useEffect, useMemo } from 'react';
import { Panel } from '../../components/panel/panel.jsx';
import CardContainer from '../../components/card/card-container.jsx';
import MarkerInfoWindowParcela from '../../components/map/marker-infowindow-parcela.jsx';
import NorthIcon from '../../components/map/north-icon.jsx';
import MapTypeToggle from '../../components/map/map-type-toggle.jsx';
import MapLegendRow from '../../components/map/map-legend-row.jsx';
import BlockUi from 'react-block-ui';
import GoogleMapReact from 'google-map-react';
import env from '../../environment';
import SelectCiclo from '../../components/select/select-ciclo.jsx';
import useEtiquetas from '../../hooks/useEtiquetas';
import transformWord from '../../utils/transformWord';
import { isMiel } from '../../components/content/coop-config';

import ParcelasResumenPlantas from '../../components/dashboards/parcelas/parcelas-resumen-plantas';
import ParcelasResumenEspecies from '../../components/dashboards/parcelas/parcelas-resumen-especies';
import ParcelasResumenOtrasActividades from '../../components/dashboards/parcelas/parcelas-resumen-otras-actividades';
import ParcelasResumenTotales from '../../components/dashboards/parcelas/parcelas-resumen-totales';
import ParcelasResumenTotalesHas from '../../components/dashboards/parcelas/parcelas-resumen-totales-has';
import './parcelas-resume.css';
import ParcelasResumenGenero from '../../components/dashboards/parcelas/parcelas-resumen-genero.js';
import ParcelasResumenColmenas from '../../components/dashboards/parcelas/parcelas-resumen-colmenas.js';
import ParcelasResumenEudrRiesgo, {
  RISK_LEVELS,
  getParcelaRiskKey,
} from '../../components/dashboards/parcelas/parcelas-resumen-eudr-riesgo.js';
import useParcelasAll from '../../hooks/useParcelasAll';
import ModalKMLDownload from '../../components/modal/modal-kml-download.jsx';
import { useCoopConfig } from '../../components/content/coop-config.jsx';

const LEGEND_MODE_OPTIONS = [
  { value: 'certificacion', label: 'Certificación' },
  { value: 'riesgo', label: 'Riesgo EUDR' },
];

const RISK_SIN_ASIGNAR = RISK_LEVELS.find(r => r.key === 'SIN_ASIGNAR');

// Maps a parcelas_all (withPoly) row to the field names used by the map markers and the KML modal
const toMapaParcela = item => ({
  ...item,
  riskKey: getParcelaRiskKey(item),
  id_parcela: item.id,
  parcela_uuid: item.uuid,
  parcela_nombre: item.nombre,
  parcela_superficie: item.superficie,
  productor_uuid: item.cuenta_uuid,
  productor_nombre: item.cuenta,
  centroide_lat: item.centroide_pol_lat,
  centroide_lng: item.centroide_pol_lng,
  poligono: item.poligono_json,
});

const ParcelasResume = () => {
  const { etiquetas } = useEtiquetas();
  const coopConfig = useCoopConfig();
  const usaEudr = !!+coopConfig.config?.usa_eudr;
  const [map, setMap] = useState(null);
  const [maps, setMaps] = useState(null);
  const [polygons, setPolygons] = useState([]);
  const [mapType, setMapType] = useState('hybrid');
  const [showKMLModal, setShowKMLModal] = useState(false);

  const [selectedElement, setSelectedElement] = useState(null);
  const [activeMarker, setActiveMarker] = useState(null);
  const [showInfoWindow, setInfoWindowFlag] = useState(true);
  const [idCiclo, setIdCiclo] = useState(0);
  const [legendMode, setLegendMode] = useState('certificacion');
  // parcelas_all (withPoly) feeds the donut cards, the map and the KML: a single request per ciclo
  const { parcelas: parcelasAll, blocking: blockingParcelasAll } = useParcelasAll(idCiclo, { withPoly: true });
  const markers = useMemo(() => parcelasAll.map(toMapaParcela), [parcelasAll]);
  const showRiesgo = usaEudr && legendMode === 'riesgo';
  const center = {
    lat: 17,
    lng: -92,
  };
  const zoom = 6;

  useEffect(() => {
    if (!map || !maps) return;
    // Clear all previous polygons in map
    polygons.map(p => p.setMap(null));
    const tempPolygons = [];
    // Add new polygons in map
    markers.forEach(function(marker) {
      if (marker.poligono !== null) {
        let poligono = JSON.parse(marker.poligono);
        let poligono_data = poligono.coordinates[0].map(function(p, index) {
          return { lat: p[1], lng: p[0] };
        });
        var polygon = new maps.Polygon({
          paths: poligono_data,
          strokeColor: '#ffffff', //marker.cuenta_categoria_color,
          strokeOpacity: 0.8,
          strokeWeight: 2,
          fillColor: '#ffffff', //marker.cuenta_categoria_color,
          fillOpacity: 0.35,
        });
        polygon.setMap(map);
        tempPolygons.push(polygon);
      }
    });
    setPolygons(tempPolygons);
  }, [map, maps, markers]);

  const getRiskLevel = element => RISK_LEVELS.find(r => r.key === element.riskKey) || RISK_SIN_ASIGNAR;

  const getMarkerColor = element => (showRiesgo ? getRiskLevel(element).color : element.cuenta_categoria_color);

  const legendItems = useMemo(() => {
    if (!markers?.length) return [];
    if (showRiesgo) {
      const counts = {};
      markers.forEach(m => {
        const key = getRiskLevel(m).key;
        counts[key] = (counts[key] || 0) + 1;
      });
      return RISK_LEVELS.map(r => ({ label: r.label, color: r.color, count: counts[r.key] || 0 }));
    }
    const groups = {};
    markers.forEach(m => {
      const key = m.cuenta_categoria || 'Sin categoría';
      if (!groups[key]) groups[key] = { label: key, color: m.cuenta_categoria_color, count: 0 };
      groups[key].count++;
    });
    return Object.values(groups);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [markers, showRiesgo]);

  const renderMap = (map, maps) => {
    setMap(map);
    setMaps(maps);
  };

  const setFilter = value => {
    setIdCiclo(value.target.id);
  };

  const onChildClick = (key, childProps) => {
    if (selectedElement && selectedElement.id === childProps.id) {
      onChildUNClick();
    } else {
      setSelectedElement(childProps);
      setActiveMarker(childProps.id);
      setInfoWindowFlag(true);
    }
  };
  const onChildUNClick = () => {
    setSelectedElement(null);
    setActiveMarker(null);
    setInfoWindowFlag(false);
  };

  // The map markers already hold the parcelas of the selected ciclo, so the KML reuses them
  const openKmlModal = () => {
    setShowKMLModal(true);
  };

  return (
    <div>
      <div className="row justify-content-between mr-1">
        <SelectCiclo
          setFilter={evt => {
            setFilter(evt);
          }}></SelectCiclo>

        {markers && markers.length > 0 && (
          <div className="align-self-center">
            <p>
              <img src={env.images.icon.kml} />
              <a
                onClick={openKmlModal}
                style={{
                  padding: 10,
                  color: '#789D16',
                  cursor: 'pointer',
                }}>
                {'Descargar Mapa'}
              </a>
            </p>
          </div>
        )}
      </div>

      {/* boxes dashboard */}
      <div className="row text-center">
        <div className="col-xl-12">
          {usaEudr ? (
            <div className="prc-cards-grid mb-3">
              <ParcelasResumenTotales parcelas={parcelasAll} blocking={blockingParcelasAll} />
              <ParcelasResumenEudrRiesgo parcelas={parcelasAll} blocking={blockingParcelasAll} />
            </div>
          ) : (
            <div className="mb-3">
              <ParcelasResumenTotales parcelas={parcelasAll} blocking={blockingParcelasAll} />
            </div>
          )}
          <div className="row card-table mb-3 bg-white">
            <div style={{ width: '100%' }}>
              <ParcelasResumenTotalesHas id_ciclo={idCiclo} />
            </div>
            <div className="row">
              <div className="col-xl-6 col-md-6">
                {isMiel() ? <ParcelasResumenEspecies id_ciclo={idCiclo} /> : <ParcelasResumenPlantas id_ciclo={idCiclo} />}
              </div>
              <div className="col-xl-6 col-md-6">
                {isMiel() ? <ParcelasResumenColmenas id_ciclo={idCiclo} /> : <ParcelasResumenOtrasActividades id_ciclo={idCiclo} />}
              </div>
            </div>
            <p className="font-lato-normal-400" style={{ width: '100%', textAlign: 'right', marginBottom: 0, marginTop: 10 }}>
              La información anterior es solamente de las {transformWord(etiquetas?.parcela, ['plural', 'lowercase'])} que tienen ficha
            </p>
          </div>
        </div>
      </div>

      {/* chart dashboard */}
      {!isMiel() && (
        <div className="row text-center mt-2 mb-4">
          <div className="col-md-12">
            <ParcelasResumenGenero id_ciclo={idCiclo} />
          </div>
        </div>
      )}

      {/* map */}
      <div className="row">
        <div className="col-xl-12">
          <CardContainer style={{ paddingTop: 16, paddingBottom: 16 }}>
            <Panel>
              <div className="row mb-2 align-items-center">
                <div className="col-6">
                  <span style={{ fontSize: 16, fontWeight: 600, color: env.colors.neutral_900 }} className="font-lato-normal-600">
                    Mapa de {transformWord(etiquetas?.parcela, ['plural', 'lowercase'])}
                  </span>
                </div>
                <div className="col-6 d-flex justify-content-end">
                  <MapTypeToggle value={mapType} onChange={setMapType} />
                </div>
              </div>

              <BlockUi tag="div" message="" blocking={blockingParcelasAll} renderChildren={true}>
                <div className="map-full-bleed" style={{ height: '650px', margin: '0 -30px', position: 'relative' }}>
                  <NorthIcon color={mapType === 'satellite' || mapType === 'hybrid' ? 'white' : 'black'} />
                  <GoogleMapReact
                    bootstrapURLKeys={{ key: env.googleApiKey }}
                    options={() => ({ mapTypeId: mapType, scaleControl: true })}
                    center={center}
                    zoom={zoom}
                    onChildClick={onChildClick}
                    onClick={onChildUNClick}
                    onGoogleApiLoaded={({ map, maps }) => renderMap(map, maps)}>
                    {markers &&
                      markers.map((element, i) => {
                        return (
                          <MarkerInfoWindowParcela
                            key={i}
                            lat={element.centroide_lat}
                            lng={element.centroide_lng}
                            color={getMarkerColor(element)}
                            productor={element.cuenta}
                            parcela={element.parcela_nombre}
                            superficie={element.parcela_superficie}
                            id={element.id_parcela}
                            selectedElement={selectedElement}
                            showInfoWindow={showInfoWindow}
                            activeMarker={activeMarker}
                            link={element.parcela_uuid ? '/admin/parcelas/perfil/' + element.parcela_uuid : null}
                            linkProductor={element.productor_uuid ? '/admin/cuentas/perfil/' + element.productor_uuid : null}
                          />
                        );
                      })}
                  </GoogleMapReact>
                </div>
                <div className="d-flex flex-wrap justify-content-between align-items-end" style={{ gap: 10 }}>
                  <MapLegendRow items={legendItems} />
                  {usaEudr && (
                    <div style={{ paddingTop: 20 }}>
                      <MapTypeToggle
                        value={legendMode}
                        onChange={setLegendMode}
                        options={LEGEND_MODE_OPTIONS}
                        ariaLabel="Tipo de leyenda"
                      />
                    </div>
                  )}
                </div>
              </BlockUi>
            </Panel>
          </CardContainer>
        </div>
      </div>
      <ModalKMLDownload isOpen={showKMLModal} onClose={() => setShowKMLModal(false)} dataForKML={markers} blocking={blockingParcelasAll} />
    </div>
  );
};

export default ParcelasResume;
